import type { AfterViewInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  Injector,
  input,
  viewChild,
  inject,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxChip } from '@onyx/angular';
import {
  OnyxAddressComponent,
  OnyxButtonComponent,
  OnyxChipsComponent,
  OnyxEmptyStateComponent,
  OnyxIconComponent,
  OnyxInformationListComponent,
  OnyxInformationRowComponent,
  OnyxMapComponent,
  OnyxMapEvent,
  OnyxMapMarkerType,
} from '@onyx/angular';
import type { Subject } from 'rxjs';
import { ContractorShippingMethodsComponent } from '../../../../../common/components/contractor-shipping-methods/contractor-shipping-methods.component';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { InvoiceType } from '../../../../../common/enums/invoice-type';
import { linkedChipSignal } from '../../../../../common/utilities/linked-chip-signal';
import { ContractorHelper } from '../../common/helpers/contractor.helper';
import type { Contractor } from '../../common/interfaces/contractor';
import type { ContractorBranch } from '../../common/interfaces/contractor-branch';

@Component({
  selector: 'app-contractor-modal-branches',
  imports: [
    OnyxMapComponent,
    TranslatePipe,
    OnyxChipsComponent,
    OnyxInformationRowComponent,
    OnyxAddressComponent,
    OnyxEmptyStateComponent,
    OnyxButtonComponent,
    OnyxIconComponent,
    ContractorShippingMethodsComponent,
    OnyxInformationListComponent,
  ],
  templateUrl: './contractor-modal-branches.component.html',
  styleUrl: './contractor-modal-branches.component.scss',
})
export class ContractorModalBranchesComponent implements AfterViewInit {
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);
  private readonly contractorHelper = inject(ContractorHelper);

  public readonly contractor = input.required<Contractor>();
  public readonly close$ = input.required<Subject<void>>();

  protected readonly I18N = 'contractors.contractorModal';

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly InvoiceType = InvoiceType;

  private readonly map = viewChild.required(OnyxMapComponent);

  protected hasEdit = computed(
    () => !ContractorHelper.isArchived(this.contractor()),
  );
  protected activeBranch = linkedChipSignal(() => this.branches());

  protected branches = computed<OnyxChip<Contractor | ContractorBranch>[]>(
    () => [
      {
        name: 'labels.mainBranch',
        value: this.contractor(),
      },
      ...this.contractor().branches.map((branch) => ({
        name: `${this.translateService.instant('labels.branch')} ${branch.name}`,
        value: branch,
      })),
    ],
  );

  protected invoiceDocuments = computed(() => {
    const defaultInvoiceDocuments = this.contractor().invoiceDocuments.find(
      ({ branchUuid }) => branchUuid === this.contractor().uuid,
    );
    const invoiceDocuments =
      this.contractor().invoiceDocuments.find(
        ({ branchUuid }) => branchUuid === this.activeBranch()[0].uuid,
      ) ?? defaultInvoiceDocuments;

    return invoiceDocuments ?? null;
  });

  protected invoice = computed(() => {
    const defaultInvoice = this.contractor().invoices.find(
      ({ branchUuid }) => branchUuid === this.contractor().uuid,
    );
    const invoice =
      this.contractor().invoices.find(
        ({ branchUuid }) => branchUuid === this.activeBranch()[0].uuid,
      ) ?? defaultInvoice;

    return invoice ?? null;
  });

  public ngAfterViewInit(): void {
    effect(
      () => {
        const map = this.map();
        const contractor = this.contractor();

        const addresses = contractor.addresses;
        const drawnMarkers = new Set<string>();

        map.dispatch(
          new OnyxMapEvent.AddUpdateMarker({
            id: 'mainAddress',
            type: OnyxMapMarkerType.BASE,
            coordinates: addresses.mainAddress.coordinates,
          }),
        );
        drawnMarkers.add('mainAddress');
        if (addresses.correspondenceAddress) {
          map.dispatch(
            new OnyxMapEvent.AddUpdateMarker({
              id: 'correspondenceAddress',
              type: OnyxMapMarkerType.CORRESPONDENCE_ADDRESS,
              coordinates: addresses.correspondenceAddress.coordinates,
            }),
          );
          drawnMarkers.add('correspondenceAddress');
        }

        for (const [index, branch] of contractor.branches.entries()) {
          const branchAddresses = branch.addresses;

          const branchMainAddressId = `branch-${index}-mainAddress`;
          map.dispatch(
            new OnyxMapEvent.AddUpdateMarker({
              id: branchMainAddressId,
              type: OnyxMapMarkerType.POINT,
              coordinates: branchAddresses.mainAddress.coordinates,
            }),
          );
          drawnMarkers.add(branchMainAddressId);

          if (branchAddresses.correspondenceAddress) {
            const branchCorrespondenceAddressId = `branch-${index}-correspondenceAddress`;
            map.dispatch(
              new OnyxMapEvent.AddUpdateMarker({
                id: branchCorrespondenceAddressId,
                type: OnyxMapMarkerType.CORRESPONDENCE_ADDRESS,
                coordinates: branchAddresses.correspondenceAddress.coordinates,
              }),
            );
            drawnMarkers.add(branchCorrespondenceAddressId);
          }
        }

        for (const id of map.markers) {
          if (drawnMarkers.has(id)) continue;
          this.map().dispatch(new OnyxMapEvent.RemoveMarker({ id }));
        }

        this.map().dispatch(new OnyxMapEvent.FitContent());
      },
      { injector: this.injector },
    );
  }

  protected edit(): void {
    this.contractorHelper.edit(this.contractor().uuid, {
      close$: this.close$(),
    });
  }
}
